import { css } from "@emotion/css";
import { Button, Chip } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon, ExclamationCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useMemo, useState } from "react";
import { isSourceMissingRequireAnnotations } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationDetails";
import type { DeploymentConfiguration } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { ArgoCDDeploymentPreviewDrawer } from "~/components/Actions/argocd/ArgoCDDeploymentPreviewDrawer";
const styles = {
    container: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
        width: "600px",
    }),
    card: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: space[16],
        borderRadius: borderRadius["large"],
        backgroundColor: themeTokens.color.background.primary.default,
        boxShadow: themeTokens.shadow.extraSmall,
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        span: {
            display: "inline-flex",
            flexDirection: "row",
            alignItems: "center",
            gap: space[8],
        },
    }),
    buttonContainer: css({
        display: "flex",
        justifyContent: "flex-end",
        paddingTop: space[16],
        paddingBottom: space[16],
    }),
};
type ArgoCDApplicationsPreviewProps = {
    applications: ArgoCDApplicationResource[];
    project: ProjectResource;
    deploymentConfiguration?: DeploymentConfiguration;
};
export const ArgoCDApplicationsPreview = (props: ArgoCDApplicationsPreviewProps) => {
    const { applications, project, deploymentConfiguration } = props;
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const handleViewPreview = () => {
        setDrawerIsOpen(true);
    };
    const closePreviewDrawer = () => {
        setDrawerIsOpen(false);
    };
    const [countSourcesRequiringGitCredentials, countSourcesMissingAnnotations] = useMemo(() => {
        let missingGitCredentials = 0;
        let missingAnnotations = 0;
        const allSources = applications.flatMap((app) => app.Sources.map((source) => ({ source, app })));
        for (const { source, app } of allSources) {
            if (source.RequiresGitCredential && !source.BestMatchedGitCredentialId) {
                missingGitCredentials++;
            }
            if (isSourceMissingRequireAnnotations(source, app)) {
                missingAnnotations++;
            }
        }
        return [missingGitCredentials, missingAnnotations];
    }, [applications]);
    return (<>
            <div className={styles.container}>
                <div className={styles.card}>
                    <span>
                        <CheckCircleIcon size={24} color={themeTokens.color.icon.success}/>
                        <span>Applications ready to update</span>
                    </span>
                    <Chip label={applications.length} variant={"filled"} backgroundColor={themeTokens.color.chip.background.primary}/>
                </div>
                {countSourcesRequiringGitCredentials > 0 && (<div className={styles.card}>
                        <span>
                            <ExclamationCircleIcon size={24} color={themeTokens.color.icon.warning}/>
                            <span>Sources missing Git credentials</span>
                        </span>
                        <Chip label={countSourcesRequiringGitCredentials} variant={"filled"} backgroundColor={themeTokens.color.chip.background.primary}/>
                    </div>)}
                {countSourcesMissingAnnotations > 0 && (<div className={styles.card}>
                        <span>
                            <ExclamationCircleIcon size={24} color={themeTokens.color.icon.warning}/>
                            <span>Sources missing annotations</span>
                        </span>
                        <Chip label={countSourcesMissingAnnotations} variant={"filled"} backgroundColor={themeTokens.color.chip.background.primary}/>
                    </div>)}

                <div className={styles.buttonContainer}>
                    <Button importance="secondary" label="View Applications" onClick={() => handleViewPreview()}/>
                </div>
            </div>
            <ArgoCDDeploymentPreviewDrawer isOpen={drawerIsOpen} onClose={() => closePreviewDrawer()} project={project} deploymentConfiguration={deploymentConfiguration}/>
        </>);
};
